import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Table, Input } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { ExaminationEntity } from '../../../store/types/prescription';
import TableWithDragableRows, {
    DragHandle,
} from '../rightPanel/workspace/padV2/TableWithDragableRows';
import { EditableCell, EditableCellV2 } from '../rightPanel/workspace/padV2/MedTableRenderer';
import { buildOptions } from '../rightPanel/workspace/padV2/SymDiaTableV2';
import { DB_TABLES } from '../../../store/dbTables';
import { GExamination } from '../../../store/db';
import { getGArray } from '../../../store/idbHandler';
import { RenderCellAutosuggestItemJsx } from '../rightPanel/workspace/LabVitalTable';
import { MedicalHistoryIcon } from '../../common/Icons';
import { fetchMatchingSymDia } from '../../../api';
import { ONE_CHAR_GENDER } from '../../../pages/NewPatient';
import { getPatientPersonalProfile } from '../../../store/selectors/prescriptionPad';
import { GlobalState } from '../../../store/reducers';
import { PatientPersonalProfile } from '../../../api/pushVisit';
import Fuse from 'fuse.js';
import { LOCAL_ID_PREFIX } from '../../../constants';
import { randomString } from '../../../store/utils';
import { getMyDbId } from '../../../api/mactiveid';

const { Column } = Table;

type Props = {
    elements: ReadonlyArray<ExaminationEntity>;
    containerId: string;
    onElementSelect: (option: ExaminationEntity, index: number) => void;
    onElementDelete: (id: string) => void;
    onDrag: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void;
    patientProfile?: PatientPersonalProfile;
};

const fuseConfig = { keys: ['name'], threshold: 0.2 };

class ExaminationFindingsTable extends PureComponent<Props> {
    TABLE_ID = 'examination-findings-table';
    gElements: ExaminationEntity[] | undefined;
    OptionIcon = MedicalHistoryIcon('fill-darwin-accent-examination-findings-sky-blue-900');
    exaFuse = new Fuse([] as GExamination[], fuseConfig);

    componentDidMount = async (): Promise<void> => {
        getGArray<GExamination>(getMyDbId() || '', DB_TABLES['examinations']).then((values) => {
            this.gElements = values;
        });
    };

    renderCellAutosuggestItem = (option: ExaminationEntity) => {
        return (
            <RenderCellAutosuggestItemJsx
                option={option}
                wrapperClassName={'examination-findings-dropdown-item'}
                icon={this.OptionIcon}
                iconWrapperClassName={'circle-examination-findings'}
            />
        );
    };

    elementSuggestApi = async (text: string): Promise<ExaminationEntity[]> => {
        const { patientProfile } = this.props;
        try {
            const allOptions: ExaminationEntity[] = [];

            const response = await fetchMatchingSymDia(
                text,
                patientProfile?.gender
                    ? ONE_CHAR_GENDER[patientProfile.gender.toLowerCase()]
                    : undefined,
            );

            if (response?.symptoms) {
                response?.symptoms.forEach((t) => {
                    if (!allOptions.find((i) => i.id === t.id)) allOptions.push(t);
                });
            }

            if (response?.diagnoses) {
                allOptions.push(...response.diagnoses);
            }

            return allOptions || [];
        } catch (e) {
            return [];
        }
    };

    findIndexForRecordKey = (key: string): number => {
        return this.props.elements.findIndex((e, index) => this.rowKeyFromId(e.id, index) === key);
    };

    createOption = (value: string) => ({
        id: `${LOCAL_ID_PREFIX}${new Date().getTime()}-${randomString(4)}`,
        name: value.trim(),
    });

    handleSearch = async (value: string): Promise<ExaminationEntity[]> => {
        const { elements } = this.props;

        const all = await buildOptions<ExaminationEntity>(
            value,
            elements,
            this.gElements || [],
            this.elementSuggestApi,
            this.exaFuse,
            this.createOption,
            false,
        );

        return all;
    };

    renderName = (_: any, __: ExaminationEntity, index: number) => {
        const { elements, containerId } = this.props;

        return {
            children: (
                <div className="flex space-x-16 items-center">
                    <DragHandle />
                    <div className="font-700 w-full text-neutral-black text-14 placeholder-neutral-6">
                        <EditableCell
                            fieldName={`efc-name-${index}`}
                            label={'Name'}
                            debounceDelay={200}
                            customDropdownClass={`py-8 examination-findings-dropdown rounded-8`}
                            customDropdownWidth={350}
                            optionsRenderer={this.renderCellAutosuggestItem}
                            onSearch={this.handleSearch}
                            getLabelToCompare={(o) => o.name}
                            selectedValue={
                                elements[index] && elements[index].id ? elements[index] : undefined
                            }
                            onSelect={(option) => {
                                option && this.props.onElementSelect(option, index);
                            }}
                            containerId={this.props.containerId}
                            placeholder={`Type property`}
                        />
                    </div>
                </div>
            ),
        };
    };

    renderNotes = (_: any, el: ExaminationEntity, index: number) => {
        const value = el.notes || '';
        return (
            <div className="flex space-x-8 items-center">
                <EditableCellV2
                    fieldName={`efc-notes-${index}`}
                    defaultValue={value}
                    onBlur={(e) => {
                        this.props.onElementSelect(
                            {
                                ...el,
                                notes: e.target.value,
                            },
                            index,
                        );
                    }}
                    label="Add notes here"
                >
                    <Input.TextArea
                        autoFocus
                        defaultValue={value}
                        rows={2}
                        placeholder="Add notes here"
                    />
                </EditableCellV2>
                <DeleteOutlined
                    style={{ color: '#616A8F' }}
                    size={24}
                    onClick={() => this.props.onElementDelete(el.id)}
                    onPointerLeaveCapture={undefined}
                />
            </div>
        );
    };

    disabledFutureDate = (current: Moment): boolean => {
        const customDate = moment().add(1, 'day').format('YYYY-MM-DD');
        return current && current > moment(customDate, 'YYYY-MM-DD');
    };

    rowKeyFromId = (id: string, index?: number): string =>
        `${'examination-findings'}-${index}-${(this.props.elements || []).length > 0}`;

    render(): ReactNode {
        const { elements, onDrag } = this.props;
        if (elements.length === 0) {
            return null;
        }

        return (
            <TableWithDragableRows
                id={this.TABLE_ID}
                showHeader={false}
                tableLayout="fixed"
                className="mt-8 lx-reading-table"
                rowClassName={() => 'editable-row'}
                bordered
                dataSource={elements}
                pagination={false}
                rowKey={(record, index) => this.rowKeyFromId(record.id, index)}
                findIndexForRecordKey={this.findIndexForRecordKey}
                onSortEnd={onDrag}
            >
                <Column
                    key="medical-history-row-name"
                    className="medical-history-row-name"
                    render={this.renderName}
                />

                <Column
                    key="medical-history-row-notes"
                    className="medical-history-row-notes"
                    render={this.renderNotes}
                />
            </TableWithDragableRows>
        );
    }
}

export const ExaminationFindingsTableComponent = connect((state: GlobalState) => ({
    patientProfile: getPatientPersonalProfile(state),
}))(ExaminationFindingsTable);
